import {
    infiniteQueryOptions,
    queryOptions,
    skipToken,
    useMutation,
    type DefaultError,
    type UseMutationResult,
} from '@tanstack/react-query';

import {
    OrganizationCreateForm,
    OrganizationData,
    OrganizationListQuery,
    OrganizationStatus,
    OrganizationUpdateForm,
} from 'chaika-frontend-types/frontend-cms';
import { OrganizationApi } from '@organization/api/OrganizationApi';
import { store } from '@store';
import { queryClient } from '@store/queryClient';
import { getUniqSortedIds } from '@utils/getUniqSortedIds';
import { loadWithChunkAndFlatten } from '@utils/loadWithChunkAndFlatten';

const STALE_TIME_MS = Infinity;

export const DEFAULT_LIST_LIMIT = 50;

export const organizationKeys = {
    all: ['organization'] as const,
    lists: () => [...organizationKeys.all, 'list'] as const,
    list: (filters: OrganizationListQuery) => [...organizationKeys.lists(), { filters }] as const,
    byIds: (ids: string[]) => [...organizationKeys.lists(), 'byIds', getUniqSortedIds(ids)] as const,
    infiniteLists: () => [...organizationKeys.all, 'infiniteList'] as const,
    infiniteList: (filters: OrganizationListQuery) => [...organizationKeys.infiniteLists(), { filters }] as const,
    details: () => [...organizationKeys.all, 'detail'] as const,
    detail: (id: string) => [...organizationKeys.details(), id] as const,
    detailWithFiles: (id: string) => [...organizationKeys.detail(id), 'withFiles'] as const,
};

export const organizationQueryOptions = {
    list: (filters: OrganizationListQuery = {}) =>
        queryOptions({
            staleTime: STALE_TIME_MS,
            queryKey: organizationKeys.list(filters),
            queryFn: (): Promise<OrganizationData[]> =>
                OrganizationApi.getList(filters).then(({ organizations }) => organizations),
        }),
    infiniteList: ({ limit, offset, ...filters }: OrganizationListQuery = {}) => {
        const pageSize = limit ?? DEFAULT_LIST_LIMIT;

        return infiniteQueryOptions({
            staleTime: STALE_TIME_MS,
            queryKey: organizationKeys.infiniteList({ ...filters, limit: pageSize }),
            queryFn: ({ pageParam }) => OrganizationApi.getList({ ...filters, limit: pageSize, offset: pageParam }),
            initialPageParam: 0,
            getNextPageParam: (lastPage, allPages) => {
                const loaded = allPages.reduce((total, page) => total + page.organizations.length, 0);
                return loaded < lastPage.count ? loaded : undefined;
            },
        });
    },
    byIds: (ids: string[]) => {
        const normalizedIds = getUniqSortedIds(ids);

        return queryOptions({
            staleTime: STALE_TIME_MS,
            queryKey: organizationKeys.byIds(normalizedIds),
            queryFn:
                normalizedIds.length > 0
                    ? () =>
                          loadWithChunkAndFlatten<OrganizationData>(normalizedIds, params =>
                              OrganizationApi.getList(params).then(({ organizations }) => organizations),
                          )
                    : skipToken,
        });
    },
    byId: (id: string | undefined) =>
        queryOptions({
            staleTime: STALE_TIME_MS,
            queryKey: organizationKeys.detail(id ?? ''),
            queryFn: id ? () => OrganizationApi.get(id) : skipToken,
        }),
    detailWithFiles: (id: string | undefined) =>
        queryOptions({
            staleTime: STALE_TIME_MS,
            queryKey: organizationKeys.detailWithFiles(id ?? ''),
            queryFn: id
                ? () =>
                      OrganizationApi.getList({ ids: id, withFiles: true }).then(
                          ({ organizations }) => organizations[0],
                      )
                : skipToken,
        }),
};

export function useCreateOrganization(): UseMutationResult<OrganizationData, DefaultError, OrganizationCreateForm> {
    return useMutation({
        mutationFn: async (params: OrganizationCreateForm) => {
            await OrganizationApi.create(params);
            return OrganizationApi.get(params.id);
        },
        onSuccess: onMutationSuccess,
    });
}

export type UpdateOrganization = (params: OrganizationUpdateForm) => Promise<OrganizationData>;

export function useUpdateOrganization(): UseMutationResult<
    OrganizationData,
    DefaultError,
    { id: string; params: OrganizationUpdateForm }
> {
    return useMutation({
        mutationFn: async ({ id, params }) => {
            await OrganizationApi.update(id, params);
            const { organizations } = await OrganizationApi.getList({ ids: id, withFiles: true });
            return organizations[0];
        },
        onSuccess: onDetailMutationSuccess,
    });
}

export function useUpdateOrganizationStatus(): UseMutationResult<
    OrganizationData,
    DefaultError,
    { id: string; status: OrganizationStatus }
> {
    return useMutation({
        mutationFn: async ({ id, status }) => {
            await OrganizationApi.updateStatus(id, status);
            return OrganizationApi.get(id);
        },
        onSuccess: onMutationSuccess,
    });
}

function onMutationSuccess(organization: OrganizationData): void {
    void queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
    void queryClient.invalidateQueries({ queryKey: organizationKeys.infiniteLists() });
    queryClient.setQueryData(organizationKeys.detail(organization.id), organization);
    // TODO: remove when MobX is not longer used as source of data for organizations
    store.organizationList.add([organization]);
}

function onDetailMutationSuccess(organization: OrganizationData): void {
    onMutationSuccess(organization);
    queryClient.setQueryData(organizationKeys.detailWithFiles(organization.id), organization);
}
